import { i18n } from "@src/app/i18n";
import { ConfigService } from "@src/services";

type DateFormatMode = "relative" | "absolute" | "period";

interface FormatDateOptions {
  mode?: DateFormatMode;
  includeTime?: boolean;
  includeSeconds?: boolean;
  endDate?: string; // для режима period
}

/**
 * Функция для форматирования дат по правилам проекта
 * @param dateString - Дата в формате ISO строки (например, "2025-11-24T21:51:04+00:00")
 * @param options - Опции форматирования
 * @returns Отформатированная строка
 */
export function formatDate(dateString: string, options: FormatDateOptions = {}) {
  const { mode = "relative", includeTime = true, includeSeconds = false, endDate } = options;

  const date = new Date(dateString);
  const now = new Date();
  const currentYear = now.getFullYear();
  const dateYear = date.getFullYear();
  const dateMonth = date.getMonth() + 1;
  const dateDay = date.getDate();

  // Проверка, является ли дата валидной
  if (isNaN(date.getTime())) {
    return "";
  }

  /**
   * Форматирует время в формате HH:mm или HH:mm:ss
   */
  const formatTime = (d: Date): string => {
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    if (includeSeconds) {
      const seconds = String(d.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    }
    return `${hours}:${minutes}`;
  };

  /**
   * Получает сокращенное название месяца
   */
  const getMonthName = (month: number): string => {
    const locale = ConfigService.getLanguage() || "ru";
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: "short" });
    const dateForMonth = new Date(2000, month - 1, 1);

    return monthFormatter.format(dateForMonth).replace(/\.$/, "").toLowerCase();
  };

  /**
   * Проверяет, является ли дата сегодняшней
   */
  const isToday = (d: Date): boolean => {
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  /**
   * Проверяет, является ли дата вчерашней
   */
  const isYesterday = (d: Date): boolean => {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      d.getDate() === yesterday.getDate() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getFullYear() === yesterday.getFullYear()
    );
  };

  /**
   * Вычисляет разницу во времени в секундах
   */
  const getTimeDifference = (d: Date): number => {
    return Math.floor((now.getTime() - d.getTime()) / 1000);
  };

  /**
   * Форматирует относительное время (для времени в прошлом)
   */
  const formatRelative = (): string => {
    const diff = getTimeDifference(date);

    // Меньше минуты
    if (diff < 60) {
      return `${diff} ${i18n.global.t("с назад")}`;
    }

    // Меньше часа
    if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      return `${minutes} ${i18n.global.t("мин назад")}`;
    }

    // Сегодня
    if (isToday(date)) {
      return formatTime(date);
    }

    // Вчера
    if (isYesterday(date)) {
      return `${i18n.global.t("Вчера")}, ${formatTime(date)}`;
    }

    // Позавчера и старше
    const monthName = getMonthName(dateMonth);
    const day = dateDay;

    if (dateYear === currentYear) {
      const time = includeTime ? `, ${formatTime(date)}` : "";
      return `${day} ${monthName}${time}`;
    } else {
      // Для несовпадающего года время не указывается
      return `${day} ${monthName} ${dateYear}`;
    }
  };

  /**
   * Форматирует абсолютную дату
   */
  const formatAbsolute = (): string => {
    const monthName = getMonthName(dateMonth);
    const day = dateDay;
    const time = includeTime ? `, ${formatTime(date)}` : "";

    if (dateYear === currentYear) {
      return `${day} ${monthName}${time}`;
    } else {
      return `${day} ${monthName} ${dateYear}${time}`;
    }
  };

  /**
   * Форматирует временной период
   */
  const formatPeriod = (): string => {
    if (!endDate) {
      return formatAbsolute();
    }

    const end = new Date(endDate);
    if (isNaN(end.getTime())) {
      return formatAbsolute();
    }

    const endYear = end.getFullYear();
    const endMonth = end.getMonth() + 1;
    const endDay = end.getDate();

    const startMonthName = getMonthName(dateMonth);
    const endMonthName = getMonthName(endMonth);

    const startTime = includeTime ? formatTime(date) : "";
    const endTime = includeTime ? formatTime(end) : "";

    // В рамках одного дня
    if (dateYear === endYear && dateMonth === endMonth && dateDay === endDay) {
      if (dateYear === currentYear) {
        return `${dateDay} ${startMonthName}, ${startTime}—${endTime}`;
      } else {
        return `${dateDay} ${startMonthName} ${dateYear}, ${startTime}—${endTime}`;
      }
    }

    // Разные даты, один год
    if (dateYear === endYear) {
      const startPart = `${dateDay} ${startMonthName}${startTime ? `, ${startTime}` : ""}`;
      const endPart = `${endDay} ${endMonthName}${endTime ? `, ${endTime}` : ""}`;
      return `${startPart} — ${endPart}`;
    }

    // Разные даты, разные годы
    const startPart = `${dateDay} ${startMonthName} ${dateYear}${startTime ? `, ${startTime}` : ""}`;
    const endPart = `${endDay} ${endMonthName} ${endYear}${endTime ? `, ${endTime}` : ""}`;
    return `${startPart} — ${endPart}`;
  };

  // Выбор функции форматирования в зависимости от режима
  switch (mode) {
    case "relative":
      return formatRelative();
    case "absolute":
      return formatAbsolute();
    case "period":
      return formatPeriod();
    default:
      return formatRelative();
  }
}
